import type { ManualChapter } from '../types';

/** Rozdział instrukcji: Customer Service (CS) (28.10.2026, S-28.10-02). */
export const chapter: ManualChapter = {
  key: 'cs',
  title: 'Customer Service (CS)',
  audience:
    'Dla osób, które przyjmują zlecenia od klientów: wpisują i uzupełniają prezlecenia, pilnują kontaktu z klientem, awizacji, raportów i zapytań.',
  intro:
    'CS jest pierwszym ogniwem zlecenia: z wiadomości albo telefonu klienta robi prezlecenie, uzupełnia braki i oddaje je do zapisu w TMS (VIA). Ten rozdział opisuje skrzynkę, prezlecenia i zlecenia z maila, a przy włączonych funkcjach działu — także obieg z Administracją, awizacje, aktualizacje dla klienta, raporty i zapytania z wyceną.',
  sections: [
    {
      id: 'jak-pracuje-cs',
      title: 'Jak pracuje CS',
      blocks: [
        {
          kind: 'text',
          text: 'Droga zlecenia od klienta wygląda tak: wiadomość w „Skrzynce” albo telefon → prezlecenie (rekord roboczy, może mieć braki) → akceptacja, z której powstaje zlecenie z naszym numerem → plik dla VIA, który po imporcie w TMS staje się zleceniem w TMS. Od akceptacji zlecenie jest też w banku zleceń, gdzie pracuje planowanie FTL i Trade.',
        },
        {
          kind: 'text',
          when: { off: ['intakeDepartments'] },
          text: 'Wszystkie czynności przyjęcia zleceń — skrzynka, prezlecenia, zlecenia z maila, zatwierdzenie i plik dla VIA — wykonuje każde konto z uprawnieniem „Przyjęcie zleceń” (administrator, administrator spółki, użytkownik). Pozostali, np. planiści i traderzy, widzą prezlecenia i zlecenia z maila tylko do podglądu.',
        },
        {
          kind: 'text',
          when: { on: ['intakeDepartments'] },
          text: 'Przyjęcie zleceń dzieli się na dwa działy. CS tworzy i uzupełnia prezlecenia (także dane dla importera VIA), prowadzi kontakt z klientem i dostaje „Uwagi od FTL”. Administracja kontroluje prezlecenie i zapisuje je do VIA plikiem — opis w rozdziale „Administracja”. Role „CS”, „Team Leader CS”, „Administracja” i „Team Leader Administracji” nadaje administrator w kontach użytkowników; konta administratora, administratora spółki i użytkownika pracują w obu działach.',
        },
        {
          kind: 'note',
          tone: 'human',
          text: 'System przygotowuje, człowiek zatwierdza. Prezlecenie akceptujesz Ty, do VIA trafia wyłącznie plik i tylko po jawnej akcji człowieka. Z aplikacji nic nie wysyła się samo — wiadomości do klienta to szkice, które wysyłasz ze swojej poczty i oznaczasz jako wysłane.',
        },
        {
          kind: 'note',
          tone: 'warning',
          text: 'Nieczytelne = puste pole. Jeśli czegoś nie wiesz, zostaw pole puste — wartość wpisana „na wszelki wypadek” wygląda później jak ustalenie z klientem i nikt jej już nie odróżni.',
        },
      ],
    },
    {
      id: 'pulpit-przyjecia-zlecen',
      title: 'Pulpit przyjęcia zleceń',
      menu: ['intake-dashboard'],
      when: { on: ['intakeDepartments'] },
      blocks: [
        {
          kind: 'text',
          text: '„Pulpit przyjęcia zleceń” w menu to miejsce, od którego CS zaczyna dzień. Każdy kafelek to kolejka z licznikiem, a kliknięcie otwiera listę z tym samym filtrem — liczba na kafelku to liczba pozycji na tej liście. Konto z rolą CS widzi tylko kafelki CS.',
        },
        {
          kind: 'image',
          image: { guide: 'intakeDepartments', step: 4 },
          caption: 'Pulpit przyjęcia zleceń — kafelki kolejek CS.',
        },
        {
          kind: 'terms',
          title: 'Kafelki CS',
          items: [
            { term: '„Do uzupełnienia (CS)”', meaning: 'Prezlecenia u CS, które trzeba uzupełnić i przekazać do Administracji.' },
            {
              term: '„Zwrócone z pytaniem (CS)”',
              meaning: 'Prezlecenia, które Administracja odesłała z pytaniem — czekają na Twoją odpowiedź.',
              when: { on: ['intakeHandoff'] },
            },
            { term: '„Uwagi od FTL — otwarte”', meaning: 'Uwagi planistów do zleceń, jeszcze niezałatwione.', when: { on: ['ftlNotes'] } },
            { term: '„Awizacje do wykonania”', meaning: 'Awizacje, których termin się zbliża albo minął.', when: { on: ['clientAvizations'] } },
            { term: '„Aktualizacje dla klienta — do wysłania”', meaning: 'Propozycje wiadomości do klienta czekające na wysłanie albo pominięcie.', when: { on: ['clientUpdates'] } },
            { term: '„Pytania do klienta — w toku”', meaning: 'Pytania o braki prezlecenia, wysłane i czekające na odpowiedź klienta.', when: { on: ['clientQuestions'] } },
            {
              term: '„Przypomnienia — po terminie, niewykonane”',
              meaning: 'Niewykonane awizacje, niewysłane aktualizacje i pytania po terminie (domyślnie 60 minut). Prowadzi do kolejki CS zawężonej do tych spraw.',
              when: { on: ['clientQuestions'] },
            },
          ],
        },
        {
          kind: 'text',
          text: 'Na liście prezleceń kolumna „Obieg” pokazuje, u którego działu jest prezlecenie i na jakim etapie, a filtr „Kolejka działu” zawęża listę tak samo jak kafelek.',
        },
      ],
    },
    {
      id: 'skrzynka',
      title: 'Skrzynka',
      menu: ['emails'],
      blocks: [
        {
          kind: 'text',
          text: '„Skrzynka” to lista wiadomości, które przyszły na skrzynki przyjęcia zleceń: kiedy, od kogo, temat, klasyfikacja, załączniki i spółka. Filtry: „Szukaj”, „Klasyfikacja”, „Z załącznikami”. Kliknięcie wiersza otwiera wiadomość: po lewej podgląd treści i załączników, po prawej szczegóły, prezlecenia z tej wiadomości, prezlecenia, których wiadomość dotyczy, i historia wątku.',
        },
        {
          kind: 'terms',
          title: 'Klasyfikacja wiadomości',
          items: [
            { term: '„Zlecenie”', meaning: 'Klient zleca transport — z takiej wiadomości powstaje prezlecenie.' },
            { term: '„Zapytanie”', meaning: 'Klient pyta o możliwość albo cenę przewozu.' },
            { term: '„Zmiana”', meaning: 'Wiadomość zmienia istniejące zlecenie — sprawdź „Dotyczy istniejących prezleceń”.' },
            { term: '„Reklamacja”, „Wewnętrzna”, „Inne”', meaning: 'Wiadomości, z których nie powstaje prezlecenie.' },
            { term: 'Procent obok klasyfikacji', meaning: 'Pewność automatycznej klasyfikacji: zielony — wysoka, pomarańczowy — średnia, czerwony — niska. Niska pewność to sygnał, żeby przeczytać wiadomość samemu.' },
          ],
        },
        {
          kind: 'task',
          title: 'Sprawdzić wiadomość i prezlecenia, które z niej powstały',
          steps: [
            'Otwórz „Skrzynka” w menu i kliknij wiadomość.',
            'Przeczytaj treść i załączniki w podglądzie po lewej.',
            'W sekcji „Prezlecenia z tego maila” sprawdź, ile prezleceń powstało i czy mają braki („braki: n” albo „komplet”).',
            'Kliknij „Otwórz” przy prezleceniu, żeby je sprawdzić i uzupełnić.',
          ],
          outcome: 'Jeśli z wiadomości nie powstało żadne prezlecenie, a powinno, załóż je ręcznie (Prezlecenia → „Utwórz”).',
        },
        {
          kind: 'task',
          title: 'Sprawdzić, kto pisał, do kogo i na jaki adres odpowiedzieć',
          when: { on: ['emailDetails'] },
          intro: 'Karta wiadomości pokazuje wszystkich uczestników maila z rolami.',
          steps: [
            'Otwórz wiadomość ze „Skrzynka”. W sekcji „Wiadomość” są: „Od”, „Wysłane w imieniu przez” (gdy ktoś wysłał w czyimś imieniu), „Do”, „DW” i „Odpowiedz do”.',
            'Odpowiadasz na adres z „Odpowiedz do”, jeśli jest — bywa inny niż „Od” (np. skrzynka działu klienta).',
            '„UDW” (ukryta kopia) widzisz tylko przy mailu wysłanym z naszej skrzynki albo gdy to nasza skrzynka dostała ukrytą kopię.',
            'Na liście skrzynki kolumna „Do/DW” pokazuje pierwszego odbiorcę i liczbę pozostałych; „Dodaj filtr” → „Nadawca” albo „Odbiorca (Do, DW)” szuka po adresie lub nazwie.',
          ],
          outcome: 'Ważność („wysoka”), odpowiedź automatyczna i wysyłka masowa są oznaczone przy wiadomości.',
        },
        {
          kind: 'task',
          title: 'Przejść po wątku i obejrzeć załączniki',
          when: { on: ['emailDetails'] },
          steps: [
            'Sekcja „Wątek” zbiera maile tej samej sprawy — także odpowiedzi z innej skrzynki. Bieżący jest wyróżniony; kliknij temat, żeby przejść do innego maila.',
            'W „Załączniki” każdy plik ma oznaczenie: „w treści” (obraz wklejony w mail), „osobny plik” albo „w załączniku” — plik z wnętrza innego, np. PDF z przesłanego maila, wcięty pod nim.',
            'Obrazy z sieci w treści są zablokowane. „Pokaż obrazy z sieci” włącza je dla tej karty — tylko gdy ufasz nadawcy, bo nadawca może po nich poznać, że mail otwarto.',
          ],
          outcome: 'Treść jest oczyszczona ze skryptów i formularzy — przyciski z maila nic nie robią w aplikacji.',
        },
        {
          kind: 'note',
          tone: 'warning',
          when: { on: ['emailDetails'] },
          text: '„Nadawca nie przeszedł weryfikacji (SPF, DKIM…)” znaczy, że serwer poczty nie potwierdził, że mail wysłał właściciel adresu — adres mógł zostać podrobiony. Mail zostaje w skrzynce; zanim na jego podstawie zmienisz zlecenie albo dane do płatności, potwierdź to u klienta telefonicznie.',
        },
        {
          kind: 'note',
          tone: 'warning',
          text: 'Przyciski „Potwierdź klasyfikację”, „Popraw dane” i „Utwórz prezlecenie” na karcie wiadomości są w przygotowaniu i jeszcze niczego nie zapisują. Do tego czasu prezlecenie zakładasz ręcznie z listy prezleceń.',
        },
      ],
    },
    {
      id: 'prezlecenia',
      title: 'Prezlecenia',
      menu: ['pre-orders'],
      blocks: [
        {
          kind: 'text',
          text: 'Prezlecenie to rekord roboczy zlecenia: klient, numer zamówienia klienta, relacja, załadunek i rozładunek (miasto, kraj, kod pocztowy, termin), wymagany typ pojazdu, ładunek, waga, palety, cena z walutą, dział i spółka. Może mieć braki — zapisać wolno niekompletne, komplet jest potrzebny dopiero do akceptacji. Lista ma filtry „Klient, miasto, towar” i „Status”; kolumna „Zlecenia” mówi, ile zleceń powstało z prezlecenia.',
        },
        {
          kind: 'image',
          image: { shot: 'pre-orders' },
          caption: 'Lista prezleceń ze statusem i liczbą braków.',
        },
        {
          kind: 'text',
          when: { on: ['sourcePreview'] },
          text: 'Na karcie prezlecenia są „Kompletność i akceptacja”, dane prezlecenia, „Akcje operatora”, zlecenia z tego prezlecenia, powiązane wiadomości i „Historia zmian (audyt)”. Mail i załączniki, z których powstało prezlecenie, otwierasz obok danych przyciskiem „Pokaż źródło” (opis niżej) — przy prezleceniach z dokumentów stoją obok danych od razu. Przy prezleceniu z maila jest też sekcja z polami odczytanymi z wiadomości — przy każdym procent pewności; pole nieczytelne zostaje puste.',
        },
        {
          kind: 'text',
          when: { off: ['sourcePreview'] },
          text: 'Na karcie prezlecenia po lewej stoi źródło (wiadomość z podglądem albo informacja, że prezlecenie wpisano ręcznie), po prawej „Kompletność i akceptacja”, dane prezlecenia, „Akcje operatora”, zlecenia z tego prezlecenia, powiązane wiadomości i „Historia zmian (audyt)”. Przy prezleceniu z maila jest też sekcja z polami odczytanymi z wiadomości — przy każdym procent pewności; pole nieczytelne zostaje puste.',
        },
        {
          kind: 'image',
          image: { shot: 'pre-order-card' },
          caption: 'Karta prezlecenia — kompletność, akceptacja i dane prezlecenia.',
        },
        {
          kind: 'terms',
          title: 'Statusy i znaczniki prezlecenia',
          items: [
            { term: '„nowe”', meaning: 'Prezlecenie wprowadzone, jeszcze nie zaakceptowane.' },
            { term: '„w weryfikacji”', meaning: 'Prezlecenie w trakcie sprawdzania. Żaden przycisk w aplikacji dziś tego statusu nie ustawia — znajdziesz go filtrem „Status”, jeśli rekord go ma.' },
            { term: '„uzupełniane”', meaning: 'Prezlecenie w trakcie uzupełniania danych. Tak jak „w weryfikacji” — żaden przycisk dziś go nie ustawia; braki pokazuje znacznik „braki: n”.' },
            { term: '„zatwierdzone”', meaning: 'Prezlecenie zaakceptowane — powstało z niego zlecenie z naszym numerem. Dalsze poprawki robi się na zleceniu.' },
            { term: '„odrzucone”', meaning: 'Prezlecenie odrzucone, nie idzie dalej i nie trafia do kolejek przyjęcia zleceń. Żaden przycisk w aplikacji dziś go nie ustawia.' },
            { term: '„braki: n”', meaning: 'Tyle pól brakuje do akceptacji; ich nazwy pokazuje „Kompletność i akceptacja”.' },
            { term: '„komplet”', meaning: 'Wszystkie pola wymagane do akceptacji są wypełnione.' },
            { term: '„zarchiwizowane”', meaning: 'Prezlecenie odłożone ręcznie z powodem; widać je tylko z filtrem „Zarchiwizowane”.', when: { on: ['preOrderArchive'] } },
            { term: '„Pytanie do klienta wysłane — czeka na odpowiedź”', meaning: 'CS wysłał klientowi pytanie o braki i czeka na odpowiedź.', when: { on: ['clientQuestions'] } },
          ],
        },
        {
          kind: 'task',
          title: 'Założyć prezlecenie ręcznie',
          intro: 'Gdy zlecenie przyszło telefonem, w pliku albo w wiadomości, z której nic nie powstało.',
          steps: [
            'Otwórz „Prezlecenia” w menu i kliknij „Utwórz”.',
            'Wybierz „Spółka” — decyduje, pod jakim identyfikatorem zlecenie pójdzie do VIA.',
            'Wpisz to, co wiadomo: klienta, numer zamówienia klienta, miejsca z krajem i kodem pocztowym, terminy, typ pojazdu, ładunek, wagę, cenę i walutę. Pola, których nie znasz, zostaw puste.',
            'Kliknij „Zapisz prezlecenie”.',
          ],
          outcome: 'Otwiera się karta prezlecenia z listą braków. Kraj i kod pocztowy są potrzebne, żeby system wyznaczył punkt na mapie, a z nim dolot w rekomendacjach — bez kraju punktu nie ma.',
        },
        {
          kind: 'task',
          title: 'Przyjąć prezlecenie z maila',
          intro: 'Prezlecenie powstało z wiadomości klienta i czeka na sprawdzenie.',
          steps: [
            'Otwórz prezlecenie z listy „Prezlecenia” albo przyciskiem „Otwórz” w „Skrzynce”.',
            'Porównaj dane z treścią wiadomości po lewej; zwróć uwagę na pola z niską pewnością i pola puste.',
            'Kliknij „Popraw dane”, uzupełnij lub popraw pola i kliknij „Zapisz zmiany”.',
            'Sprawdź „Kompletność i akceptacja” — gdy nie ma braków, kliknij „Akceptuj i utwórz zlecenie”.',
          ],
          outcome: 'Powstaje zlecenie z naszym numerem (otwiera się jego karta w „Zlecenia z maila”), a wiersz w banku zleceń przechodzi do planowania. Każda zmiana pola trafia do historii prezlecenia.',
        },
        {
          kind: 'note',
          tone: 'human',
          text: 'Akceptacja to osobna, jawna decyzja — zapisanie formularza niczego nie akceptuje. „Akceptuj i utwórz zlecenie” jest nieaktywne, dopóki są braki. Po akceptacji prezlecenia nie poprawia się już na prezleceniu, tylko na zleceniu, które z niego powstało.',
        },
        {
          kind: 'text',
          when: { off: ['intakeHandoff'] },
          text: 'Po akceptacji to samo konto przyjęcia zleceń uzupełnia „Dane dla importera VIA” na zleceniu, sprawdza i zatwierdza plik — opis w sekcji „Zlecenia z maila” i w rozdziale „Administracja”.',
        },
        {
          kind: 'text',
          when: { on: ['preOrderContractor'] },
          text: 'Pole „Kontrahent z kartoteki”: kliknij „Wybierz z kartoteki” i wyszukaj klienta po nazwie, numerze klienta w TMS albo NIP. System podpowiada z nazwy klienta, wybierasz Ty; przy kilku oddziałach z jednym NIP pojawia się ostrzeżenie. Z kontrahentem liczy się rekomendowana stawka VAT jeszcze przed połączeniem z TMS. Klient spoza kartoteki zostaje samą nazwą.',
        },
        {
          kind: 'text',
          when: { on: ['preOrderAgreementSource'] },
          text: 'Przy „Popraw dane” wskaż „Źródło ustalenia” — telefon, mail poza systemem, ustalenie ustne albo inne — i dopisz krótki opis (przy „inne” obowiązkowy). Przy zmienionym polu karty stoi potem „źródło: telefon — opis”, a źródło trafia do historii pola.',
        },
        {
          kind: 'text',
          when: { on: ['requiredFieldsHighlight'] },
          text: 'Nazwy pól obowiązkowych są półgrube — na karcie, w „Popraw dane” i w „Danych dla importera”. To ta sama lista, która blokuje akceptację i przekazanie do Administracji.',
        },
        {
          kind: 'task',
          title: 'Dołączyć plik od klienta',
          when: { on: ['preOrderAttachments'] },
          intro: 'Gdy prezlecenie wpisujesz z zamówienia w PDF, arkusza, zdjęcia albo zapisanej wiadomości.',
          steps: [
            'Na karcie prezlecenia, w sekcji „Pliki od klienta”, kliknij „Dołącz plik od klienta”.',
            'Wybierz plik z dysku (do 20 plików przy prezleceniu; największy rozmiar pokazuje sekcja).',
            'Sprawdź dane prezlecenia z dokumentem — „Podgląd” otwiera PDF i obraz na karcie, resztę pobierasz przyciskiem „Pobierz”.',
          ],
          outcome: 'Pomyłkowy plik usuniesz przed akceptacją przyciskiem „Usuń” z powodem (co najmniej 10 znaków); po akceptacji plik zostaje. Plik może być źródłem ustalenia przy ręcznej poprawce.',
        },
        {
          kind: 'task',
          title: 'Sprawdzić daną w źródle: mail i załączniki',
          when: { on: ['sourcePreview'], off: ['preOrderDocuments'] },
          intro: 'Gdy w prezleceniu brakuje danej albo nie dało się jej odczytać — na przykład godziny załadunku.',
          steps: [
            'Na karcie prezlecenia kliknij „Pokaż źródło” — obok danych otworzy się mail, z którego powstało prezlecenie, i jego załączniki.',
            'Przełączaj zakładki: „Mail” z treścią wiadomości, potem po jednej na każdy załącznik i plik od klienta.',
            'Przy polu bez wartości albo odczytanym niepewnie kliknij „pokaż w źródle” — otworzy się załącznik, z którego to pole pochodzi.',
            'Wpisz albo popraw wartość w danych prezlecenia przez „Popraw dane”. Źródła nie da się zmienić.',
          ],
          outcome: 'PDF i zdjęcie zobaczysz na miejscu; arkusz i dokument pobierasz przyciskiem „Pobierz oryginał”. Treść maila jest pokazana bez skryptów i bez obrazów z internetu. Źródło ukryjesz tym samym przyciskiem — wybór zostaje zapamiętany w przeglądarce.',
        },
        { kind: 'image', when: { on: ['sourcePreview'] }, image: { guide: 'sourcePreview', step: 2 }, caption: 'Dane prezlecenia po jednej stronie, mail i załączniki po drugiej.' },
        {
          kind: 'text',
          when: { on: ['preOrderDocuments'] },
          text: 'Wszystkie pola prezlecenia są opcjonalne — zapisujesz to, co wiadomo, a nieczytelne zostawiasz puste. Komplet jest potrzebny dopiero do utworzenia zlecenia i do zapisu w TMS. Karta „Gotowe do wysłania” mówi, czego brakuje, pogrupowane: do utworzenia zlecenia, dane do pliku, zleceniodawca, ładunek i punkty; brakujące pola są zaznaczone przy danych prezlecenia i w sekcji „Dane dla importera TMS”. Na liście prezleceń kolumna „Stan” pokazuje „Gotowe do wysłania” albo „do TMS brakuje: n”, a kolumny „Maile”, „Pliki” i „Źródło” — ile maili i plików ma prezlecenie i skąd pochodzi.',
        },
        {
          kind: 'task',
          title: 'Uzupełnić dane dla importera TMS na prezleceniu',
          when: { on: ['preOrderDocuments'] },
          intro: 'Gdy z maila albo dokumentu znasz adres zleceniodawcy, punkty z oknami czasowymi, jednostki i objętość.',
          steps: [
            'Na karcie prezlecenia przejdź do sekcji „Dane dla importera TMS”.',
            'Wpisz zleceniodawcę z adresem i objętość przesyłki. Punktów nie ma — kliknij „Punkty z danych prezlecenia” (przepisze miasto, kraj, kod i początek okna) albo „Dodaj punkt”.',
            'Przy każdym punkcie uzupełnij firmę, ulicę, oba końce okna i jednostki. Pól, których nie da się odczytać, nie wypełniaj.',
            'Kliknij „Zapisz dane dla importera”.',
          ],
          outcome: 'Lista braków w „Gotowe do wysłania” odświeża się od razu; każda zmiana pola trafia do historii prezlecenia. Po utworzeniu zlecenia te dane poprawia się już w zleceniu.',
        },
        {
          kind: 'task',
          title: 'Obejrzeć dokument: PDF, arkusz, skan, zdjęcie',
          when: { on: ['preOrderDocuments'] },
          steps: [
            'Otwórz kartę prezlecenia — dane są po lewej, dokument od razu po prawej (przy wąskim oknie pod danymi). „Zwiń podgląd dokumentu” go chowa; wybór zostaje zapamiętany dla Twojego konta.',
            'Przy polu bez wartości albo odczytanym niepewnie kliknij „pokaż w źródle” — otworzy się załącznik, z którego to pole pochodzi.',
            'Przełączaj zakładki albo strzałki „poprzedni / następny materiał”.',
            'PDF przewijasz stronami i powiększasz w pasku nad dokumentem. Zdjęcie i skan powiększasz, pomniejszasz i obracasz przyciskami nad obrazem; wielostronicowy skan TIFF przełączasz strzałkami stron. Arkusz XLSX, XLS albo CSV widzisz jako tabelę z kolejnymi arkuszami.',
          ],
          outcome: 'Arkusz pokazuje same wartości — formuł nie przeliczamy, makr nie uruchamiamy. Zdjęcie HEIC (z iPhone’a) aplikacja przelicza na zwykły obraz — powiększasz je i obracasz jak każde inne; gdy w pliku jest kilka zdjęć, pokazuje pierwsze i mówi o tym, a oryginał pobierasz przyciskiem „Pobierz oryginał”. Plik, którego zawartość nie zgadza się z nazwą (na przykład strona internetowa zapisana jako PDF), nie otworzy się w aplikacji — tylko do pobrania.',
        },
        {
          kind: 'task',
          title: 'Powiązać mail z prezleceniem albo go odpiąć',
          when: { on: ['preOrderDocuments'] },
          intro: 'Jeden mail może dotyczyć kilku prezleceń (lista ładunków), a jedno prezlecenie kilku maili (zamówienie, uzupełnienie, korespondencja).',
          steps: [
            'Na karcie prezlecenia, w sekcji „Maile prezlecenia”, kliknij „Powiąż mail”.',
            'Wyszukaj mail po temacie, nadawcy albo treści, wybierz rolę — źródło, uzupełnienie albo korespondencja — i kliknij „Powiąż”.',
            'Pomyłkowe powiązanie usuniesz przyciskiem „Odepnij” — podaj powód (co najmniej 10 znaków).',
            'Z karty maila przejdziesz do każdego prezlecenia przyciskiem „Otwórz prezlecenie”; tam też powiążesz mail z prezleceniem.',
          ],
          outcome: 'Powiązanie widać na obu kartach. W historii prezlecenia zostaje wpis: który mail, w jakiej roli, kto i kiedy, a przy odpięciu — dlaczego. Mail, z którego przyjęto zaakceptowane prezlecenie, zostaje.',
        },
        { kind: 'image', when: { on: ['preOrderDocuments'] }, image: { guide: 'preOrderDocuments', step: 5 }, caption: 'Dane prezlecenia po lewej, dokument po prawej.' },
        {
          kind: 'task',
          title: 'Skopiować prezlecenie (stałe zlecenie klienta)',
          when: { on: ['preOrderCopy'] },
          steps: [
            'Na karcie prezlecenia kliknij „Kopiuj”.',
            'Przy każdym polu wybierz: zostaw, zmień albo wyczyść. Daty i numer zamówienia klienta wymagają Twojej decyzji — numer nie przechodzi na kopię sam.',
            'Kliknij „Utwórz kopię”.',
          ],
          outcome: 'Kopia jest zwykłym prezleceniem do akceptacji; w historii obu stoi „skopiowane z” i „skopiowano do”.',
        },
        {
          kind: 'task',
          title: 'Zapytać klienta o braki',
          when: { on: ['clientQuestions'] },
          steps: [
            'Na karcie prezlecenia kliknij „Przygotuj pytanie do klienta”.',
            'Zaznacz pola, o które pytasz (braki są zaznaczone od razu), sprawdź adresata i kliknij „Przygotuj szkic”.',
            'Popraw treść, kliknij „Otwórz w poczcie” albo „Kopiuj”, wyślij wiadomość ze swojej poczty i kliknij „Wysłane ręcznie”.',
            'Gdy klient odpowie, wpisz odpowiedź i kliknij „Wpisz odpowiedź”; dane popraw przez „Popraw dane”.',
          ],
          outcome: 'Pytanie, którego już nie potrzebujesz, odwołasz przyciskiem „Odwołaj pytanie” z powodem. Niewysłane po terminie trafia do przypomnień.',
        },
        {
          kind: 'task',
          title: 'Zarchiwizować prezlecenie',
          when: { on: ['preOrderArchive'] },
          intro: 'Prezlecenie nie wygasa samo — gdy klient zrezygnował albo to duplikat, odłóż je ręcznie.',
          steps: [
            'Na karcie prezlecenia kliknij „Archiwizuj”.',
            'Wpisz powód (co najmniej 10 znaków) i kliknij „Zarchiwizuj”.',
          ],
          outcome: 'Prezlecenie znika z listy, kolejek i pulpitu, a wiersz banku przechodzi do „Nieaktualne”. „Przywróć” (też z powodem) cofa archiwizację. Prezlecenia zaakceptowanego zarchiwizować nie można.',
        },
      ],
    },
    {
      id: 'obieg-z-administracja',
      title: 'Obieg prezlecenia CS → Administracja',
      when: { on: ['intakeHandoff'] },
      blocks: [
        {
          kind: 'text',
          text: 'Prezlecenie ma etap obiegu, widoczny w sekcji „Obieg CS → Administracja” na karcie: „u CS”, „u Administracji”, „zwrócone do CS z pytaniem”, „zatwierdzone przez Administrację”. Dane zmienia tylko dział, u którego jest rekord — drugi widzi podgląd. Przy prezleceniu stoi licznik zwrotów.',
        },
        {
          kind: 'image',
          image: { guide: 'intakeHandoff', step: 3 },
          caption: 'Przekazanie prezlecenia do Administracji.',
        },
        {
          kind: 'task',
          title: 'Przekazać prezlecenie do Administracji',
          steps: [
            'Zaakceptuj prezlecenie („Akceptuj i utwórz zlecenie”).',
            'Uzupełnij pola wymienione w sekcji „Obieg CS → Administracja” — bez nich importer VIA nie przyjmie pliku; brakujące wpisujesz w „Dane dla importera VIA” na zleceniu.',
            'Kliknij „Przekaż do Administracji”, w polu „Uwaga dla Administracji” wpisz uwagę i kliknij „Przekaż”.',
          ],
          outcome: 'Prezlecenie jest u Administracji: od tej chwili ona zmienia dane, a Ty widzisz podgląd. FTL i Trade widzą etap w banku.',
        },
        {
          kind: 'task',
          title: 'Odpowiedzieć na pytanie Administracji',
          steps: [
            'Kliknij kafelek „Zwrócone z pytaniem (CS)” na pulpicie i otwórz prezlecenie.',
            'Przeczytaj pytanie w sekcji „Obieg CS → Administracja”.',
            'Popraw dane („Popraw dane” albo „Dane dla importera VIA”), jeśli pytanie tego wymaga.',
            'Kliknij „Odpowiedz i przekaż ponownie”, w polu „Odpowiedź” wpisz odpowiedź i kliknij „Odpowiedz i przekaż”.',
          ],
          outcome: 'Pytanie i odpowiedź zostają w historii prezlecenia. Zwrot cofa zatwierdzenie zapisu — Administracja zatwierdza od nowa.',
        },
        {
          kind: 'text',
          when: { on: ['intakeQuestionField'] },
          text: 'Pytanie może dotyczyć konkretnego pola — wtedy karta obiegu podaje „Pytanie Administracji do pola …”, przy polu stoi znacznik z treścią pytania, a w formularzu pytanie jest pod polem. Znacznik znika po odpowiedzi i ponownym przekazaniu.',
        },
        {
          kind: 'image',
          image: { guide: 'intakeHandoff', step: 5 },
          caption: 'Zwrócone prezlecenie czeka na pulpicie CS.',
        },
        {
          kind: 'note',
          tone: 'system',
          text: 'Przekazanie, pytania i odpowiedzi to zapisy w aplikacji — nic nie wychodzi pocztą.',
        },
      ],
    },
    {
      id: 'zlecenia-z-maila',
      title: 'Zlecenia z maila',
      menu: ['orders'],
      blocks: [
        {
          kind: 'text',
          text: '„Zlecenia z maila” to zlecenia z naszym numerem, które powstały z zaakceptowanych prezleceń — z maila albo wpisanych ręcznie. Lista ma filtry „Nr, klient, relacja”, „Stan zapisu VIA” i „Źródło”. Karta zlecenia pokazuje dane, odsyłacz „W banku zleceń” i do prezlecenia, zlecenie w VIA (gdy import z TMS je przyniósł), rekordy zapisu do VIA oraz sekcję „Dane dla importera VIA”.',
        },
        {
          kind: 'terms',
          title: 'Stan zapisu VIA',
          items: [
            { term: '„Szkic”', meaning: 'Plik jeszcze nie powstał; dane można zmieniać.' },
            { term: '„Zatwierdzone do zapisu”', meaning: 'Ktoś zatwierdził treść zlecenia do pliku. Zmiana danych cofa zlecenie do szkicu i kasuje zatwierdzenie.' },
            { term: '„Plik wytworzony”, „Dostarczone”', meaning: 'Plik powstał i trafił do folderu importu VIA.' },
            { term: '„Jest w TMS”', meaning: 'Import z TMS przyniósł zlecenie z naszym numerem — zapis się udał.' },
            { term: '„Błąd zapisu”', meaning: 'Plik nie został dostarczony; trzeba go ponowić.' },
          ],
        },
        {
          kind: 'task',
          title: 'Uzupełnić dane dla importera VIA',
          intro: 'Nagłówek zlecenia to tylko część pól, których wymaga importer VIA — resztę uzupełnia przyjęcie zleceń.',
          steps: [
            'Otwórz zlecenie w „Zlecenia z maila”.',
            'W sekcji „Dane dla importera VIA” kliknij „Otwórz dane”.',
            'Kliknij „Wypełnij z danych, które mamy”, a potem uzupełnij resztę ręcznie — puste pole zostaw puste, jeśli wartości nie znasz.',
            'Kliknij „Zapisz”.',
          ],
          outcome: 'Zlecenie z wózkiem (typ z „+FORKLIFT”) dostaje sekcję czynności wózkowej — do VIA pójdzie wtedy drugi plik.',
        },
        {
          kind: 'text',
          when: { off: ['intakeDepartments'] },
          text: 'Na karcie zlecenia jest też panel „Zapis do VIA”: „Sprawdź plik”, „Zatwierdź” i „Wytwórz plik”. Obsługę pliku opisuje rozdział „Administracja”.',
        },
        {
          kind: 'text',
          when: { on: ['intakeDepartments'] },
          text: 'Konto z rolą CS zamiast panelu zapisu widzi informację, że zatwierdzenie i plik dla VIA należą do Administracji.',
        },
        {
          kind: 'note',
          tone: 'human',
          text: 'Do VIA trafia wyłącznie plik i tylko po jawnej akcji człowieka. Po przyjęciu zlecenia w VIA zmiana oznacza usunięcie i wprowadzenie ponowne — nie nadpisanie.',
        },
        {
          kind: 'text',
          when: { on: ['preOrderStageInBank'] },
          text: 'Planiści i Trade widzą etap Twojego prezlecenia w banku zleceń (znacznik w kolumnie „TMS”, filtr „Etap prezlecenia”) i na karcie zlecenia w sekcji „Etap prezlecenia” — tylko do odczytu. Dzięki temu nie muszą dzwonić z pytaniem, czy zlecenie jest już w TMS.',
        },
      ],
    },
    {
      id: 'anulowanie-zlecenia',
      title: 'Anulowanie zlecenia',
      when: { on: ['intakeOrderCancellation'] },
      blocks: [
        {
          kind: 'task',
          title: 'Anulować zlecenie odwołane przez klienta',
          steps: [
            'Otwórz kartę zlecenia albo prezlecenia i kliknij „Anuluj zlecenie”.',
            'Przeczytaj, co się stanie, wpisz powód (co najmniej 10 znaków, np. „Klient odwołał transport mailem”) i kliknij „Anuluj zlecenie”.',
            'Usuń zlecenie w TMS samodzielnie, a potem kliknij „Usunięte w TMS” na karcie.',
          ],
          outcome: 'Zlecenie przechodzi do „Nieaktualne”, pojazd i warianty są zwalniane, oferty z giełd schodzą same, a FTL i Trade dostają powiadomienie. Karta pokazuje „Anulowane w aplikacji — do usunięcia w TMS”.',
        },
        {
          kind: 'note',
          tone: 'warning',
          text: 'Do TMS nic nie wysyłamy — zlecenie w TMS usuwa człowiek. Jeśli po „Usunięte w TMS” import nadal je widzi, karta pokazuje rozbieżność; trzeba sprawdzić zlecenie w TMS.',
        },
      ],
    },
    {
      id: 'uwagi-od-ftl',
      title: 'Uwagi od FTL',
      menu: ['ftl-notes'],
      when: { on: ['ftlNotes'] },
      blocks: [
        {
          kind: 'text',
          text: 'Planista albo Team Leader FTL zostawia na karcie zlecenia uwagę dla przyjęcia zleceń: kategoria (np. „Opóźnienie i ETA”, niedostępność kierowcy, załadunek i rozładunek, dane zlecenia, „Inne”), treść i znacznik „pilna”. Lista „Uwagi od FTL” w menu ma licznik otwartych, otwarte na górze.',
        },
        {
          kind: 'task',
          title: 'Obsłużyć uwagę od FTL',
          steps: [
            'Otwórz „Uwagi od FTL” w menu i kliknij uwagę.',
            'Załatw sprawę — np. zadzwoń do klienta.',
            'Wybierz stan „w toku” albo „załatwiona”, dopisz komentarz i kliknij „Zapisz stan”.',
          ],
          outcome: 'Planista widzi Twój komentarz przy uwadze; każda zmiana stanu trafia do historii zlecenia. Nic nie wychodzi pocztą.',
        },
      ],
    },
    {
      id: 'kolejka-cs',
      title: 'Kolejka CS i notatki przy zleceniu',
      menu: ['cs-queue'],
      when: { on: ['csLight'] },
      blocks: [
        {
          kind: 'text',
          text: '„Kolejka CS” zbiera sprawy działu w jednym miejscu: prezlecenia do uzupełnienia i zwrócone z pytaniem, otwarte uwagi od FTL i zmiany zleceń czekające na decyzję CS. Na górze najbliższy termin załadunku. Filtry: klient, lokalizacja, „Rodzaj sprawy”, „Wykonawca” (flota, przewoźnik, Trade, nieprzydzielone).',
        },
        {
          kind: 'terms',
          title: 'Krytyczność',
          items: [
            { term: 'Pilne', meaning: 'Termin w ciągu 24 h albo już minął, albo pilna uwaga FTL.' },
            { term: 'Wysokie', meaning: 'Termin w ciągu 72 h albo zwrot z pytaniem.' },
            { term: 'Przypomnienie', meaning: 'Niewykonana awizacja, niewysłana aktualizacja albo pytanie po terminie.', when: { on: ['clientQuestions'] } },
          ],
        },
        {
          kind: 'image',
          image: { guide: 'csLight', step: 2 },
          caption: 'Kolejka CS — sprawy według terminu i krytyczności.',
        },
        {
          kind: 'task',
          title: 'Zapisać wynik rozmowy z klientem przy zleceniu',
          steps: [
            'Na karcie zlecenia rozwiń sekcję 7 „Komunikacja z klientem”.',
            'Wybierz kanał (telefon, mail, platforma) i wynik: potwierdzono termin, prośba o zmianę terminu, brak kontaktu — ponowić albo inny wynik z opisem. Możesz też dodać samą notatkę.',
            'Kliknij „Zapisz przy zleceniu”.',
          ],
          outcome: 'Kto i kiedy zapisuje się samo; wpis widzą też planiści i Trade. Z systemu nic nie jest wysyłane — zapisujesz to, co ustaliłeś.',
        },
      ],
    },
    {
      id: 'awizacje',
      title: 'Awizacje',
      menu: ['client-avizations', 'avization-calendar'],
      when: { on: ['clientAvizations'] },
      blocks: [
        {
          kind: 'text',
          text: 'Awizacja to zadanie przy zleceniu: punkt (załadunek albo rozładunek), kanał (telefon, mail, platforma klienta, plik Excel), termin i kontakt. Zakłada ją CS przy zleceniu albo powstaje sama z reguły awizacji klienta. „Kolejka awizacji” pokazuje zadania według terminu, „Kalendarz telefonów” — awizacje telefoniczne dnia według godziny.',
        },
        {
          kind: 'image',
          image: { guide: 'clientAvizations', step: 1 },
          caption: 'Kolejka awizacji według terminu.',
        },
        {
          kind: 'task',
          title: 'Zaawizować telefonicznie',
          steps: [
            'Otwórz „Kolejka awizacji” (albo „Kalendarz telefonów” i „zadzwoń →”) i otwórz awizację.',
            'Zadzwoń do rozmówcy — numer i tekst do przekazania skopiujesz przyciskiem „Kopiuj”.',
            'Wybierz „Wynik rozmowy”, dopisz notatkę i kliknij „Zapisz wynik rozmowy”.',
          ],
          outcome: '„brak kontaktu — ponowić” zostawia awizację do wykonania i liczy próbę. Wynik trafia do sekcji 7 zlecenia i do historii.',
        },
        {
          kind: 'task',
          title: 'Zaawizować mailem albo na platformie klienta',
          steps: [
            'Otwórz awizację z kolejki.',
            'Mail: popraw szkic, kliknij „Otwórz w poczcie” albo „Kopiuj”, wyślij ze swojej poczty i kliknij „Wysłane ręcznie”.',
            'Platforma albo plik Excel: skopiuj pola przyciskami „Kopiuj”, wprowadź je u klienta i kliknij „Oznacz jako wprowadzone”.',
          ],
          outcome: 'Czego nie wiemy, tego w treści nie ma — puste pole to brak danych. Awizację, której nie trzeba robić, zamkniesz przyciskiem „Pomiń z powodem”.',
        },
        {
          kind: 'task',
          title: 'Dodać awizację albo regułę awizacji klienta',
          steps: [
            'Przy jednym zleceniu: w sekcji 7 „Komunikacja z klientem” kliknij „Dodaj awizację”, uzupełnij „Punkt”, „Kanał”, „Termin awizacji” i „Kontakt” i kliknij „Dodaj do kolejki”.',
            'Na stałe dla klienta: na karcie kontrahenta, w sekcji „Reguła awizacji klienta (CS)”, kliknij „Zmień regułę” i wpisz kanał, punkty, „Ile godzin przed oknem”, kontakt, właściciela i „Obowiązuje od”.',
            'Wpisz powód zmiany i kliknij „Zapisz regułę”.',
          ],
          outcome: 'Reguła sama zakłada awizacje dla zleceń klienta. Bez reguły powstają tylko awizacje dodane ręcznie — system niczego nie zgaduje.',
        },
        {
          kind: 'note',
          tone: 'system',
          text: 'Po zmianie pojazdu, terminu albo miejsca (także po zaakceptowanej zmianie zlecenia) system zakłada ponowną awizację: zastępuje niewykonaną albo dochodzi po wykonanej. Awizacja po terminie dostaje przypomnienie.',
        },
      ],
    },
    {
      id: 'aktualizacje-dla-klienta',
      title: 'Aktualizacje dla klienta',
      menu: ['client-updates'],
      when: { on: ['clientUpdates'] },
      blocks: [
        {
          kind: 'text',
          text: 'Zmiana pojazdu, przewoźnik z TMS, zmiana terminu w TMS, zaakceptowana zmiana zlecenia i anulowanie dają propozycję aktualizacji dla klienta z gotowym szkicem wiadomości. Lista „Aktualizacje dla klienta” pokazuje zlecenie, klienta, zdarzenie, czy klient wymaga tej informacji i stan. Nowa propozycja tego samego rodzaju zastępuje niewysłaną.',
        },
        {
          kind: 'task',
          title: 'Przygotować aktualizację dla klienta (szkic, wysyłasz sam)',
          steps: [
            'Otwórz „Aktualizacje dla klienta” w menu.',
            'Przy propozycji kliknij „Podgląd i wyślij” i popraw treść; adresat pochodzi z kontaktu zlecenia.',
            'Kliknij „Otwórz w poczcie” albo „Kopiuj treść” i wyślij wiadomość ze swojej poczty.',
            'Kliknij „Wysłane ręcznie”.',
          ],
          outcome: 'W historii zlecenia zostaje kto i kiedy oznaczył wysyłkę. Gdy klient tej informacji nie potrzebuje, kliknij „Pomiń z powodem”.',
        },
        {
          kind: 'text',
          text: 'O czym klient chce wiedzieć, mówi reguła na karcie kontrahenta — sekcja „Reguła aktualizacji dla klienta (CS)”, przycisk „Wpisz regułę”, z właścicielem i datą obowiązywania. Bez reguły powstają wszystkie propozycje z oznaczeniem „bez reguły klienta”.',
        },
        {
          kind: 'note',
          tone: 'human',
          text: 'Z aplikacji nic nie jest wysyłane. „Wysłane ręcznie” to Twoje oświadczenie, że wiadomość wyszła z Twojej poczty.',
        },
      ],
    },
    {
      id: 'konfiguracja-raportow',
      title: 'Konfiguracja raportów dla klientów',
      menu: ['client-report-configs'],
      when: { on: ['clientReportConfig'] },
      blocks: [
        {
          kind: 'text',
          text: 'Konfiguracja mówi, jaki raport dostaje klient: zakres zleceń (po dacie załadunku albo rozładunku, dni względem dnia raportu), lokalizacje (puste — wszystkie), pola, częstotliwość i godzinę wysyłki (czas polski), adresatów i format pliku (Excel albo CSV). Dane kierowcy i numer terminala dodaj tylko wtedy, gdy klient ich wymaga.',
        },
        {
          kind: 'task',
          title: 'Skonfigurować raport klienta',
          steps: [
            'Otwórz „Konfiguracja raportów” w menu i kliknij „Nowa konfiguracja”.',
            'Wybierz klienta z kartoteki, wpisz nazwę raportu, zakres, częstotliwość, „Godzina wysyłki”, format, właściciela, „Obowiązuje od”, adresatów, lokalizacje i zakres pól.',
            'Wpisz powód i kliknij „Zapisz konfigurację”.',
          ],
          outcome: 'Każda zmiana wymaga powodu; przycisk „Historia” pokazuje, kto, kiedy i co zmienił. Konfiguracje są puste, dopóki CS ich nie wpisze — formatów raportów nie zgadujemy.',
        },
      ],
    },
    {
      id: 'raporty-dla-klientow',
      title: 'Raporty dla klientów',
      menu: ['client-reports'],
      when: { on: ['clientReportQueue'] },
      blocks: [
        {
          kind: 'text',
          text: 'Raporty czekają w kolejce według terminu. Powstają z harmonogramu konfiguracji (dwie godziny przed godziną wysyłki) albo od razu przyciskiem „Przygotuj teraz”. W podglądzie przy każdym terminie stoi „plan” albo „potwierdzone” — potwierdzone podaje TMS w realizacji; braki są na czerwono.',
        },
        {
          kind: 'terms',
          title: 'Stany raportu',
          items: [
            { term: 'do przygotowania', meaning: 'Raport czeka na przygotowanie danych.' },
            { term: 'niekompletny', meaning: 'W raporcie są braki — wysyłka tylko z potwierdzeniem, że wychodzi z oznaczonymi brakami.' },
            { term: 'do akceptacji', meaning: 'Raport gotowy do przejrzenia i wysłania.' },
            { term: 'wysłany ręcznie', meaning: 'CS oznaczył, że wysłał raport ze swojej poczty.' },
            { term: 'wstrzymany', meaning: 'Raport odłożony z powodem; „Wznów” przywraca go do kolejki.' },
            { term: '„po terminie”', meaning: 'Termin wysyłki minął.' },
          ],
        },
        {
          kind: 'task',
          title: 'Przygotować raport dla klienta',
          steps: [
            'Otwórz „Raporty dla klientów” w menu i kliknij raport (albo „Przygotuj teraz”).',
            'Przejrzyj podgląd: terminy „plan” i „potwierdzone”, braki na czerwono.',
            'Kliknij „Pobierz Excel” albo „Pobierz CSV”, a potem „Otwórz w poczcie” albo „Kopiuj treść”.',
            'Wyślij wiadomość z plikiem jako załącznikiem ze swojej poczty i kliknij „Akceptuję — wysłane ręcznie”. Przy brakach zaznacz „Wysłałem raport z oznaczonymi brakami (n)” i kliknij „Wysłane z oznaczonymi brakami”.',
          ],
          outcome: 'Przed wysłaniem możesz odświeżyć dane („Przygotuj ponownie”). Historia raportu mówi, kto go przygotował, pobrał plik, wstrzymał i oznaczył wysyłkę.',
        },
        {
          kind: 'image',
          image: { guide: 'clientReportQueue', step: 3 },
          caption: 'Plik i szkic wiadomości — wysyłasz sam.',
        },
        {
          kind: 'note',
          tone: 'human',
          text: 'Raport nie wychodzi automatycznie. Plan nie jest pokazywany jako dane rzeczywiste, a wysyłkę zawsze akceptuje człowiek.',
        },
      ],
    },
    {
      id: 'zapytania-i-wyceny',
      title: 'Zapytania klienta i wycena',
      menu: ['inquiries'],
      when: { on: ['clientInquiries'] },
      blocks: [
        {
          kind: 'text',
          text: 'Zapytanie klienta to ten sam rekord co prezlecenie, tylko w stanie „zapytanie” — bez wiersza w banku i poza kolejkami. Lista „Zapytania” ma filtry, np. „Otwarte”, „Czekają na zatwierdzenie ceny”, „Przekazane do FTL”, „Sygnał do Trade”.',
        },
        {
          kind: 'terms',
          title: 'Stany zapytania',
          items: [
            { term: 'do sprawdzenia', meaning: 'Zapytanie założone, nikt jeszcze nie sprawdził dostępności ani ceny.' },
            { term: 'wycenione', meaning: 'Jest zatwierdzona cena.' },
            { term: 'oferta wysłana', meaning: 'CS oznaczył, że wysłał ofertę albo potwierdzenie.' },
            { term: 'przyjęte, odrzucone, brak odpowiedzi', meaning: 'Wynik oferty od klienta.' },
            { term: 'odmowa', meaning: 'Marathon odmówił realizacji z powodem.' },
          ],
        },
        {
          kind: 'task',
          title: 'Założyć zapytanie klienta i sprawdzić dostępność aut',
          steps: [
            'Otwórz „Zapytania” w menu i kliknij „Nowe zapytanie” — formularz jak przy prezleceniu; kliknij „Zapisz zapytanie”.',
            'Na karcie zapytania kliknij „Sprawdź dostępność aut” — wynik liczy ten sam rachunek co rekomendacje pojazdów.',
            'Przy wybranym aucie kliknij „Wskaż wstępnie” (to tylko znacznik CS — nie blokuje auta i nie jest wariantem).',
            'Zdecyduj: „Przekaż do FTL do decyzji” (planista odpowie „możliwe” albo „niemożliwe”), „Sygnał: lepiej Trade” albo „Odmów z powodem”.',
          ],
          outcome: '„Potwierdź klientowi możliwość realizacji” otwiera szkic — kopiujesz go albo otwierasz w poczcie, wysyłasz sam i oznaczasz „Wysłane ręcznie”.',
        },
        {
          kind: 'image',
          image: { guide: 'clientInquiries', step: 2 },
          caption: 'Dostępność aut dla zapytania.',
        },
        {
          kind: 'task',
          title: 'Wycenić zapytanie',
          when: { on: ['inquiryQuotes'] },
          steps: [
            'Na karcie zapytania sprawdź podstawę wyceny: trasa, historia cen z TMS na relacji, koszt własny floty, historia klienta.',
            'Wpisz „Cena dla klienta (netto)”, sprawdź marżę i kliknij „Zaproponuj cenę”.',
            'Poczekaj, aż dział Trade kliknie „Zatwierdź cenę” (inna cena tylko z powodem).',
            'Kliknij „Przygotuj ofertę”, otwórz szkic w poczcie albo skopiuj, wyślij sam i oznacz „Wysłane ręcznie”.',
          ],
          outcome: 'Cenników nie mamy — wycena jest zawsze SPOT, bez wymyślonych cen. Bez zatwierdzenia ceny oferty nie ma; nowa wycena zastępuje poprzednią, stara zostaje w historii.',
        },
        {
          kind: 'task',
          title: 'Zapisać wynik oferty',
          when: { on: ['inquiryOutcomes'] },
          steps: [
            'Na karcie zapytania z wysłaną ofertą wybierz wynik: „przyjęta”, „odrzucona” albo „brak odpowiedzi”.',
            'Przy odrzuceniu i braku odpowiedzi wpisz powód (np. cena, brak auta w terminie, klient wybrał innego przewoźnika) i kliknij „Zapisz wynik”.',
          ],
          outcome: '„przyjęta” robi z zapytania prezlecenie w tym samym rekordzie, z danymi i ceną zatwierdzoną; przy komplecie pól powstaje wiersz w banku w stanie „Prezlecenie”, dalej zwykły obieg.',
        },
        {
          kind: 'note',
          tone: 'human',
          text: 'Cenę proponuje CS, zatwierdza dział Trade. Ofertę i potwierdzenie wysyłasz sam ze swojej poczty — system przygotowuje tylko szkic.',
        },
      ],
    },
    {
      id: 'historia-wycen',
      title: 'Historia wycen',
      menu: ['inquiry-history'],
      when: { on: ['inquiryOutcomes'] },
      blocks: [
        {
          kind: 'text',
          text: '„Historia wycen” pokazuje każdą wycenę — także zastąpione — z ceną, wynikiem i powodem. U góry liczniki: „Przyjęte”, „Odrzucone”, „Bez odpowiedzi” i „Najczęstszy powód odrzucenia”.',
        },
        {
          kind: 'task',
          title: 'Sprawdzić, dlaczego klienci odrzucają oferty',
          steps: [
            'Otwórz „Historia wycen” w menu.',
            'Spójrz na licznik „Najczęstszy powód odrzucenia” i przejrzyj odrzucone wyceny.',
          ],
        },
      ],
    },
    {
      id: 'wnioski-o-wyjatek',
      title: 'Wnioski traderów o wyjątek od reguły klienta',
      when: { on: ['subcontractorExceptionByCs'] },
      blocks: [
        {
          kind: 'text',
          text: 'Gdy klient zakazuje podwykonawców albo dopuszcza ich warunkowo, trader przed publikacją na giełdzie składa wniosek o wyjątek. Rozstrzyga go CS i Team Leader CS (jak dotąd także administratorzy). Lista „Wnioski o wyjątek” w grupie „Giełdy” ma licznik czekających, a o nowym wniosku dostajesz powiadomienie.',
        },
        {
          kind: 'task',
          title: 'Rozstrzygnąć wniosek o wyjątek',
          steps: [
            'Rozwiń w menu grupę „Giełdy” i otwórz „Wnioski o wyjątek”.',
            'Sprawdź zlecenie i regułę klienta.',
            'Kliknij „Zatwierdź” (uwagi opcjonalne) albo „Odrzuć” z powodem.',
          ],
          outcome: 'Decyzja trafia do historii zlecenia; zatwierdzony wniosek pozwala traderowi zlecić publikację. Własnych wniosków się nie rozstrzyga. Szczegóły bramek publikacji — w rozdziale „Trade”.',
        },
      ],
    },
  ],
};
